import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../../../services/shared/apiService';
import { useAuth } from '../../../context/shared/AuthContext';
import { BellIcon, CalendarIcon, TagIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface Alert {
  id: string;
  patientId?: string;
  patientName?: string;
  alertType: string;
  symbolicTags: string[];
  createdAt: string;
  acknowledged: boolean;
  notes?: string;
  read: boolean;
  alertLevel?: 'critical' | 'warning' | 'info';
}

const mockAlerts: Alert[] = [
    { id: '1', alertType: 'Emotional Distress', symbolicTags: ['sadness', 'anxiety'], createdAt: '2024-05-22T14:30:00Z', acknowledged: false, read: false, alertLevel: 'critical' },
    { id: '2', alertType: 'Missing Data', symbolicTags: ['no_entry'], createdAt: '2024-05-20T10:00:00Z', acknowledged: true, read: true, alertLevel: 'warning' },
    { id: '3', alertType: 'Wellness Trend', symbolicTags: ['sleep_decline'], createdAt: '2024-05-18T08:00:00Z', acknowledged: false, read: true, alertLevel: 'info' },
    { id: '4', alertType: 'Emotional Distress', symbolicTags: ['anger'], createdAt: '2024-05-15T18:00:00Z', acknowledged: true, read: true, alertLevel: 'warning' },
];

const AlertHistoryPanel: React.FC<{ patientId: string }> = ({ patientId }) => {
    const { user } = useAuth();
    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [filter, setFilter] = useState<string>('all');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAlerts = async () => {
            if (!user) return;
            try {
                // const response = await apiService.getPatientAlerts(user.uid, patientId);
                // setAlerts(response.alerts);
                setAlerts(mockAlerts);
            } catch (err) {
                console.error('Using mock data due to error:', err);
                // setError('Failed to load alerts. Displaying sample data.');
                setAlerts(mockAlerts);
            } finally {
                setIsLoading(false);
            }
        };
        fetchAlerts();
    }, [patientId, user]);

    const alertTypes = useMemo(() => ['all', ...Array.from(new Set(alerts.map(a => a.alertType)))], [alerts]);
    
    const filteredAlerts = useMemo(() => {
        if (filter === 'all') return alerts;
        return alerts.filter(a => a.alertType === filter);
    }, [alerts, filter]);

    if (isLoading) return <div className="text-center p-4">Loading alerts...</div>;
    if (error) return <div className="text-center p-4 text-red-500">{error}</div>;

    return (
        <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Alert History</h2>
            <div className="flex space-x-2 mb-4 border-b border-gray-200 pb-2">
                {alertTypes.map(type => (
                    <button
                        key={type}
                        onClick={() => setFilter(type)}
                        className={`px-3 py-1 text-sm font-semibold rounded-full ${filter === type ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        {type}
                    </button>
                ))}
            </div>
            <div className="space-y-4">
                {filteredAlerts.map(alert => (
                    <div key={alert.id} className={`p-3 rounded-lg border-l-4 ${alert.alertLevel === 'critical' ? 'border-red-500 bg-red-50' : 'border-yellow-500 bg-yellow-50'}`}>
                        <div className="flex items-center font-semibold text-gray-800">
                            {alert.alertLevel === 'critical' && <ExclamationTriangleIcon className="w-5 h-5 mr-2 text-red-500" />}
                            {alert.alertType}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 flex items-center space-x-4">
                            <span className="flex items-center"><CalendarIcon className="w-4 h-4 mr-1" />{new Date(alert.createdAt).toLocaleDateString()}</span>
                            <span className="flex items-center"><TagIcon className="w-4 h-4 mr-1" />{alert.symbolicTags.join(', ')}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AlertHistoryPanel; 